import React, { useState } from "react";
import Icon from "../../../components/AppIcon";

const SolutionSection = () => {
  const [activeFeature, setActiveFeature] = useState("dashboard");

  const features = [
    {
      id: "dashboard",
      title: "Unified Dashboard",
      description:
        "Get complete business overview across all locations in real-time",
      icon: "LayoutDashboard",
      benefits: [
        "Real-time sales tracking",
        "Multi-location overview",
        "Key performance metrics",
        "Quick action buttons",
      ],
    },
    {
      id: "pos",
      title: "Smart POS System",
      description: "Professional point-of-sale with inventory integration",
      icon: "CreditCard",
      benefits: [
        "Fast checkout process",
        "Automatic inventory updates",
        "Multiple payment methods",
        "Receipt generation",
      ],
    },
    {
      id: "inventory",
      title: "Inventory Management",
      description: "Never run out of stock with intelligent inventory tracking",
      icon: "Package",
      benefits: [
        "Low stock alerts",
        "Automatic reorder points",
        "Supplier management",
        "Cost tracking",
      ],
    },
    {
      id: "analytics",
      title: "Business Analytics",
      description: "Make data-driven decisions with comprehensive reports",
      icon: "BarChart3",
      benefits: [
        "Sales performance",
        "Profit margins",
        "Customer insights",
        "Trend analysis",
      ],
    },
  ];

  const mockScreens = {
    dashboard: {
      title: "Business Dashboard",
      stats: [
        {
          label: "Today's Sales",
          value: "$7,400",
          change: "+12%",
          positive: true,
        },
        { label: "Items Sold", value: "156", change: "+8%", positive: true },
        { label: "Locations", value: "2", change: "Active", positive: true },
      ],
      recentActivity: [
        { action: "Sale #1234", amount: "$260", time: "2 min ago" },
        { action: "Sale #1232", amount: "$180", time: "5 min ago" },
        { action: "Sale #1233", amount: "$360", time: "8 min ago" },
      ],
    },
    pos: {
      title: "Point of Sale",
      items: [
        { name: "Coffee Beans 1kg", price: "$135", qty: 2 },
        { name: "Espresso Cup", price: "$36", qty: 1 },
        { name: "Milk 1L", price: "$18", qty: 3 },
      ],
      total: "$360",
    },
    inventory: {
      title: "Inventory Status",
      items: [
        { name: "Coffee Beans", stock: 45, status: "good", alert: false },
        { name: "Paper Cups", stock: 12, status: "low", alert: true },
        { name: "Sugar Packets", stock: 89, status: "good", alert: false },
        { name: "Napkins", stock: 5, status: "critical", alert: true },
      ],
    },
    analytics: {
      title: "Sales Analytics",
      chartData: [
        { day: "Mon", sales: 15000 },
        { day: "Tue", sales: 18000 },
        { day: "Wed", sales: 22000 },
        { day: "Thu", sales: 19000 },
        { day: "Fri", sales: 24500 },
      ],
    },
  };

  const activeScreen = mockScreens?.[activeFeature];
  const activeFeatureData = features?.find((f) => f?.id === activeFeature);

  return (
    <section id="solution-section" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-success/10 rounded-full text-success text-sm font-medium mb-6">
            <Icon name="CheckCircle" size={16} className="mr-2" />
            Complete Business Solution
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-text-primary mb-6">
            One App. Complete Control.
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Transform your business operations with our comprehensive mobile app
            that combines POS, inventory management, analytics, and
            multi-location support in one powerful platform.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Feature Tabs */}
          <div className="order-2 lg:order-1">
            <div className="space-y-4">
              {features?.map((feature) => (
                <div
                  key={feature?.id}
                  className={`cursor-pointer rounded-xl p-6 transition-all duration-300 ${
                    activeFeature === feature?.id
                      ? "bg-primary/5 border-2 border-primary shadow-lg"
                      : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
                  }`}
                  onClick={() => setActiveFeature(feature?.id)}
                >
                  <div className="flex items-start">
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                        activeFeature === feature?.id
                          ? "bg-primary text-white"
                          : "bg-white text-text-secondary"
                      }`}
                    >
                      <Icon name={feature?.icon} size={24} />
                    </div>
                    <div className="flex-1">
                      <h3
                        className={`text-lg font-semibold mb-2 ${
                          activeFeature === feature?.id
                            ? "text-primary"
                            : "text-text-primary"
                        }`}
                      >
                        {feature?.title}
                      </h3>
                      <p className="text-text-secondary mb-4">
                        {feature?.description}
                      </p>

                      {activeFeature === feature?.id && (
                        <div className="space-y-2">
                          {feature?.benefits?.map((benefit, index) => (
                            <div key={index} className="flex items-center">
                              <Icon
                                name="Check"
                                size={16}
                                className="text-success mr-2"
                              />
                              <span className="text-sm text-text-secondary">
                                {benefit}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Icon
                      name="ChevronRight"
                      size={20}
                      className={`transition-transform ${
                        activeFeature === feature?.id
                          ? "rotate-90 text-primary"
                          : "text-text-secondary"
                      }`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile App Preview */}
          <div className="order-1 lg:order-2">
            <div className="relative mx-auto max-w-sm">
              {/* Phone Frame */}
              <div className="relative bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
                <div className="bg-white rounded-[2rem] overflow-hidden">
                  {/* Status Bar */}
                  <div className="bg-gray-900 text-white text-xs px-6 py-2 flex justify-between items-center">
                    <span>9:41</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Signal" size={12} />
                      <Icon name="Wifi" size={12} />
                      <Icon name="Battery" size={12} />
                    </div>
                  </div>

                  {/* App Header */}
                  <div className="bg-primary text-white px-6 py-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">{activeScreen?.title}</h3>
                      <Icon name="MoreVertical" size={20} />
                    </div>
                  </div>

                  {/* Dynamic Content */}
                  <div className="p-4 h-80 bg-gray-50 overflow-y-auto">
                    {activeFeature === "dashboard" && (
                      <div className="space-y-4">
                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-3">
                          {activeScreen?.stats?.map((stat, index) => (
                            <div
                              key={index}
                              className="bg-white rounded-lg p-3 shadow-sm"
                            >
                              <div className="text-lg font-bold text-text-primary">
                                {stat?.value}
                              </div>
                              <div className="text-xs text-text-secondary">
                                {stat?.label}
                              </div>
                              <div
                                className={`text-xs ${
                                  stat?.positive ? "text-success" : "text-error"
                                }`}
                              >
                                {stat?.change}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <h4 className="font-medium text-text-primary mb-3">
                            Recent Activity
                          </h4>
                          <div className="space-y-2">
                            {activeScreen?.recentActivity?.map(
                              (activity, index) => (
                                <div
                                  key={index}
                                  className="flex justify-between items-center text-sm"
                                >
                                  <span className="text-text-secondary">
                                    {activity?.action}
                                  </span>
                                  <div className="text-right">
                                    <div className="font-medium">
                                      {activity?.amount}
                                    </div>
                                    <div className="text-xs text-text-secondary">
                                      {activity?.time}
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {activeFeature === "pos" && (
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <h4 className="font-medium text-text-primary mb-3">
                            Current Sale
                          </h4>
                          <div className="space-y-2">
                            {activeScreen?.items?.map((item, index) => (
                              <div
                                key={index}
                                className="flex justify-between items-center text-sm"
                              >
                                <div>
                                  <div className="font-medium">
                                    {item?.name}
                                  </div>
                                  <div className="text-text-secondary">
                                    Qty: {item?.qty}
                                  </div>
                                </div>
                                <div className="font-medium">{item?.price}</div>
                              </div>
                            ))}
                          </div>
                          <div className="border-t pt-2 mt-3">
                            <div className="flex justify-between items-center font-bold">
                              <span>Total</span>
                              <span className="text-primary">
                                {activeScreen?.total}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button className="w-full bg-primary text-white rounded-lg py-3 font-medium">
                          Process Payment
                        </button>
                      </div>
                    )}

                    {activeFeature === "inventory" && (
                      <div className="space-y-3">
                        {activeScreen?.items?.map((item, index) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg p-3 shadow-sm"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-medium text-text-primary">
                                  {item?.name}
                                </div>
                                <div className="text-sm text-text-secondary">
                                  Stock: {item?.stock}
                                </div>
                              </div>
                              <div className="flex items-center">
                                {item?.alert && (
                                  <Icon
                                    name="AlertTriangle"
                                    size={16}
                                    className="text-warning mr-2"
                                  />
                                )}
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    item?.status === "good"
                                      ? "bg-success/10 text-success"
                                      : item?.status === "low"
                                      ? "bg-warning/10 text-warning"
                                      : "bg-error/10 text-error"
                                  }`}
                                >
                                  {item?.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeFeature === "analytics" && (
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <h4 className="font-medium text-text-primary mb-3">
                            Weekly Sales
                          </h4>
                          <div className="space-y-2">
                            {activeScreen?.chartData?.map((data, index) => (
                              <div key={index} className="flex items-center">
                                <span className="text-sm text-text-secondary w-8">
                                  {data?.day}
                                </span>
                                <div className="flex-1 bg-gray-200 rounded-full h-2 mx-2">
                                  <div
                                    className="bg-primary h-2 rounded-full"
                                    style={{
                                      width: `${(data?.sales / 25000) * 100}%`,
                                    }}
                                  ></div>
                                </div>
                                <span className="text-sm font-medium">
                                  ${data?.sales?.toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Feature Indicator */}
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-primary text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                <Icon
                  name={activeFeatureData?.icon}
                  size={16}
                  className="inline mr-2"
                />
                {activeFeatureData?.title}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-lg text-text-secondary mb-6">
            Ready to transform your business operations?
          </p>
          <button
            onClick={() =>
              document
                .getElementById("trial-signup")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center px-8 py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-cta"
          >
            Start Now
            <Icon name="ArrowRight" size={20} className="ml-2" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
